/**
 * @module  js/stores/accountsstore.js
 * @class   AccountsStore
 * @summary Central cache layer for CRM accounts and deals with reactive updates.
 *
 * @description
 * Fetches accounts, pipelines, and per-user news preferences, flattening CRM
 * results into UI-friendly rows. Provides a publish-and-subscribe API plus
 * mutators for news-frequency and task-email settings.
 *
 * ## Key Features
 * – Loads accounts, pipelines, and news-preference docs in parallel
 * – Flattens nested deal structures into account-level rows for tables
 * – Offers reactive subscription model to notify controllers on data refresh
 *
 * ## Public API
 * – `subscribe(fn)` — register a listener for account-list changes
 * – `getAccounts(force)` — fetch and cache flattened account rows
 * – `updateNewsFrequency(accountId, name, freq)` — patch an account’s email cadence
 * – `toggleTasksInEmails(enabled)` — global on/off for task digests
 *
 * ## Dependencies
 * { ApiClient, AuthService, CRMConnectionService, endpoints, FirestoreService }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

export default class AccountsStore {
  #apiClient;
  #endpoints; // ← new
  #authService;
  #crmConnection;
  #firestoreService;

  #rawAccounts = null;
  #flattenedAccounts = null;
  #pipelines = [];
  #accountNewsMap = {};
  #subscribers = new Set();
  #cacheUid = null;
  #cacheCrm = null;

  constructor({
    apiClient,
    endpoints,
    authService,
    crmConnectionService,
    firestoreService,
  }) {
    if (
      !apiClient ||
      !endpoints ||
      !authService ||
      !crmConnectionService ||
      !firestoreService
    ) {
      throw new Error('AccountsStore: missing dependencies');
    }
    this.#apiClient = apiClient;
    this.#endpoints = endpoints;
    this.#authService = authService;
    this.#crmConnection = crmConnectionService;
    this.#firestoreService = firestoreService;
  }

  /* ─────────── Public API ─────────── */

  subscribe(fn) {
    this.#subscribers.add(fn);
    if (this.#flattenedAccounts) fn(this.#flattenedAccounts);
    return () => this.#subscribers.delete(fn);
  }

  async getAccounts(force = false) {
    return this.getAccountsForUser({}, force);
  }

  /**
   * Fetch accounts for an arbitrary user (admin impersonation helper).
   * @param {{ userId?:string, crm?:string, tenant?:string }} context
   * @param {boolean} [force=false]
   */
  async getAccountsForUser(context = {}, force = false) {
    const uid = context.userId ?? (await this.#authService.requireAuth()).uid;
    const crm =
      context.crm ?? (await this.#crmConnection.getConnectedProvider(uid));
    const tenant = context.tenant ?? null;

    if (
      !force &&
      this.#flattenedAccounts &&
      this.#cacheUid === uid &&
      this.#cacheCrm === crm
    ) {
      return this.#flattenedAccounts;
    }

    await this.#loadPipelines({ userId: uid, crm, tenant });
    await this.#loadAccountNewsPrefs(uid);
    await this.#loadAccountsFromCRM({ userId: uid, crm, tenant });

    this.#flattenedAccounts = this.#flatten(this.#rawAccounts);
    this.#cacheUid = uid;
    this.#cacheCrm = crm;
    this.#emit();
    return this.#flattenedAccounts;
  }

  async updateNewsFrequency(accountId, accountName, frequency) {
    const updatedProps = {
      name: accountName,
      email_frequency: frequency,
      email_content: frequency === 'none' ? '' : 'news',
    };
    await this.#patchAccountDetails(accountId, updatedProps);

    if (this.#flattenedAccounts) {
      for (const row of this.#flattenedAccounts) {
        if (row.accountId === accountId) row.newsFrequency = frequency;
      }
      this.#emit();
    }
  }

  async toggleTasksInEmails(enabled) {
    const updatedProps = { email_content: enabled ? 'tasks' : '' };
    await this.#patchAccountDetails('GLOBAL_TASKS_TOGGLE', updatedProps);
  }

  /* ─────────── Internals ─────────── */

  #emit() {
    for (const fn of this.#subscribers) fn(this.#flattenedAccounts);
  }

  async #loadAccountsFromCRM({ userId, crm, tenant } = {}) {
    const uid = userId ?? (await this.#authService.requireAuth()).uid;
    const provider =
      crm ?? (await this.#crmConnection.getConnectedProvider(uid));
    const url = this.#endpoints.getAccountsByOwner({
      crm: provider,
      userId: uid,
      ...(tenant ? { tenant } : {}),
    });
    const json = await this.#apiClient.fetchJson(url, { auth: true });
    if (!json?.results)
      throw new Error('AccountsStore: malformed CRM response');
    this.#rawAccounts = json.results;
  }

  async #loadPipelines({ userId, crm, tenant } = {}) {
    const uid = userId ?? (await this.#authService.requireAuth()).uid;
    const provider =
      crm ?? (await this.#crmConnection.getConnectedProvider(uid));
    const url = this.#endpoints.getPipelines({
      crm: provider,
      userId: uid,
      ...(tenant ? { tenant } : {}),
    });
    const json = await this.#apiClient.fetchJson(url, { auth: true });
    this.#pipelines = json?.pipelines ?? [];
  }

  async #loadAccountNewsPrefs(userId) {
    const uid = userId ?? (await this.#authService.requireAuth()).uid;
    const doc = await this.#firestoreService.getDocument(
      `UserPreferences/${uid}`
    );
    this.#accountNewsMap = doc?.accountNews ?? {};
  }

  async #patchAccountDetails(accountId, updatedProps) {
    const user = await this.#authService.requireAuth();
    const crm = await this.#crmConnection.getConnectedProvider();
    const url = this.#endpoints.updateAccountDetails({ crm });
    await this.#apiClient.fetchJson(url, {
      method: 'POST',
      body: { userId: user.uid, accountId, crm, updatedProps },
      auth: true,
    });
  }

  #flatten(crmResults = []) {
    const out = [];
    for (const acct of crmResults) {
      const accountId = String(acct.id);
      const accountName = acct.properties?.name ?? 'Unnamed Account';
      const notesUpdated = acct.properties?.notes_last_updated ?? '';
      const newsFreq =
        this.#accountNewsMap[accountId]?.email_frequency ?? 'none';

      const pushRow = (deal = null) =>
        out.push({
          accountId,
          accountName,
          notesLastUpdated: notesUpdated,
          dealId: deal?.id ?? null,
          dealName: deal?.properties?.dealname ?? (deal ? '(No name)' : null),
          amount: deal?.properties?.amount ?? '',
          probability: deal?.properties?.hs_forecast_probability ?? '',
          closeDate: deal?.properties?.closedate ?? '',
          dealStage: this.#stageLabel(deal?.properties?.dealstage ?? ''),
          newsFrequency: newsFreq,
        });

      if (acct.deals?.length) acct.deals.forEach(pushRow);
      else pushRow(); // account without deals
    }
    return out;
  }

  #stageLabel(internal) {
    for (const pipe of this.#pipelines) {
      const s = pipe.stages?.find((st) => st.stageId === internal);
      if (s) return s.label;
    }
    return internal;
  }
}
